import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Brain, Heart, Zap, Shield, Moon, Bone, Sparkles, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

const PLACEHOLDER_EXAMPLES = [
  "I want more bowel movements...",
  "I'm doing a no-sugar challenge...",
  "Anti-inflammatory foods only...",
  "Meal prep for a 3-day hike...",
  "Protein-maxxing...",
  "I'm bulking...",
];

// Validation patterns for custom goal input
const GIBBERISH_PATTERNS = [
  /^[^a-zA-Z]*$/, // No letters at all
  /(.)\1{4,}/, // Same character repeated 5+ times (e.g., "aaaaa")
  /^[a-z]{1,2}$/i, // Too short (1-2 chars)
  /^[\W\d_]+$/, // Only symbols/numbers
  /[^\x00-\x7F]{5,}/, // Too many non-ASCII characters in a row
];

const PROMPT_INJECTION_PATTERNS = [
  /ignore\s*(all|previous|prior|above)/i,
  /disregard\s*(all|previous|prior|above)/i,
  /forget\s*(all|previous|prior|above)/i,
  /act\s+as\s+(a|an)?/i,
  /you\s+are\s+now/i,
  /pretend\s+(to\s+be|you('re|are))/i,
  /write\s+(me\s+)?(a\s+)?(code|script|program)/i,
  /jailbreak/i,
  /\bDAN\b/,
  /system\s*prompt/i,
  /\binstructions?\b.*\b(ignore|override|bypass)/i,
  /\bprompt\b.*\b(inject|hack|break)/i,
];

// Inappropriate/offensive content patterns
const INAPPROPRIATE_PATTERNS = [
  /\b(fuck|shit|damn|ass|bitch|crap)\b/i,
  /\b(sex|porn|nude|naked)\b/i,
  /\b(kill|murder|suicide|die)\b/i,
  /\b(drug|cocaine|heroin|meth)\b/i,
];

// Check if text appears to be primarily English (Latin alphabet)
const isLikelyEnglish = (text: string): boolean => {
  const latinChars = text.match(/[a-zA-Z]/g) || [];
  const totalAlphaChars = text.match(/\p{L}/gu) || [];
  if (totalAlphaChars.length === 0) return false;
  // At least 70% of letters should be Latin
  return latinChars.length / totalAlphaChars.length >= 0.7;
};

// Check for excessive random characters / keyboard mashing
const isKeyboardMashing = (text: string): boolean => {
  const consonantCluster = /[bcdfghjklmnpqrstvwxyz]{5,}/i;
  const randomPattern = /[qwxz]{3,}/i;
  const repeatedKeys = /(.)\1{3,}/;
  return consonantCluster.test(text) || randomPattern.test(text) || repeatedKeys.test(text);
};

// Validate custom goal is not gibberish, malicious, or inappropriate
const validateCustomGoal = (goal: string): { isValid: boolean; message?: string } => {
  const trimmed = goal.trim();
  
  // Check minimum length
  if (trimmed.length < 3) {
    return { isValid: false, message: "Please enter a more descriptive goal" };
  }
  
  // Check for gibberish patterns
  for (const pattern of GIBBERISH_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { isValid: false, message: "Please enter a valid health or dietary goal" };
    }
  }
  
  // Check for prompt injection attempts
  for (const pattern of PROMPT_INJECTION_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { isValid: false, message: "I can only help with grocery and health-related goals" };
    }
  }
  
  // Check for inappropriate content
  for (const pattern of INAPPROPRIATE_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { isValid: false, message: "Please keep your goal appropriate and health-focused" };
    }
  }
  
  // Check if input is primarily English
  if (!isLikelyEnglish(trimmed)) {
    return { isValid: false, message: "Please enter your goal in English" };
  }
  
  // Check for keyboard mashing
  if (isKeyboardMashing(trimmed)) {
    return { isValid: false, message: "Please enter a valid health or dietary goal" };
  }
  
  // Must contain at least one recognizable word (3+ letters)
  const words = trimmed.split(/\s+/);
  const hasValidWord = words.some(word => /^[a-zA-Z]{3,}$/.test(word.replace(/[^\w]/g, '')));
  if (!hasValidWord) {
    return { isValid: false, message: "Please describe your health goal in words" };
  }
  
  return { isValid: true };
};

interface GoalSelectionProps {
  onComplete: (goals: string[], customGoal?: string) => void;
}

const CONSENT_VERSION = "v1.0";
const MAX_GOALS = 3;
const goals = [{
  id: "gut",
  label: "Gut & Digestion",
  icon: Sparkles,
  description: "Fiber & probiotics"
}, {
  id: "brain",
  label: "Brain & Focus",
  icon: Brain,
  description: "Omega-3s & B vitamins"
}, {
  id: "heart",
  label: "Heart & Cardiovascular",
  icon: Heart,
  description: "Potassium & magnesium"
}, {
  id: "energy",
  label: "Energy & Vitality",
  icon: Zap,
  description: "Iron & complex carbs"
}, {
  id: "immunity",
  label: "Immunity & Defense",
  icon: Shield,
  description: "Vitamin C & zinc"
}, {
  id: "sleep",
  label: "Sleep & Recovery",
  icon: Moon,
  description: "Magnesium & tryptophan"
}, {
  id: "bones",
  label: "Bones & Joints",
  icon: Bone,
  description: "Calcium & vitamin D"
}, {
  id: "skin",
  label: "Skin & Radiance",
  icon: Sparkles,
  description: "Antioxidants & collagen"
}];

export const GoalSelection = ({
  onComplete
}: GoalSelectionProps) => {
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [customGoal, setCustomGoal] = useState<string>("");
  const [isShaking, setIsShaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const { toast } = useToast();
  
  // Cycle through placeholder examples
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  
  const isAtLimit = selectedGoals.length >= MAX_GOALS;
  const hasCustomGoal = customGoal.trim().length > 0;
  const hasSelectedGoals = selectedGoals.length > 0;
  
  const toggleGoal = (goalId: string) => {
    // If custom goal has text, clear it when user clicks a card
    if (hasCustomGoal) {
      setCustomGoal("");
    }
    
    const isSelected = selectedGoals.includes(goalId);

    // If trying to add when at limit, show soft block
    if (!isSelected && isAtLimit) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      toast({
        title: "Limit reached",
        description: "Please deselect a goal to add a new one.",
        duration: 3000
      });
      return;
    }
    setSelectedGoals(prev => {
      if (isSelected) {
        return prev.filter(id => id !== goalId);
      }
      return [...prev, goalId];
    });
  };
  
  const handleCustomGoalChange = (value: string) => {
    // If user types in custom goal, clear selected cards
    if (value.trim().length > 0 && hasSelectedGoals) {
      setSelectedGoals([]);
    }
    setCustomGoal(value);
  };
  
  const handleContinue = async () => {
    if (!hasSelectedGoals && !hasCustomGoal) return;
    
    setIsSubmitting(true);
    
    if (hasCustomGoal) {
      // Validate custom goal before submitting
      const validation = validateCustomGoal(customGoal);
      if (!validation.isValid) {
        toast({
          title: "Invalid Goal",
          description: validation.message,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }
      
      // Custom goal path - pass empty array with custom goal string
      setIsSubmitting(false);
      onComplete([], customGoal.trim());
    } else {
      // Standard goals path
      const sanitizedGoals = selectedGoals.filter(id => goals.some(g => g.id === id)).slice(0, MAX_GOALS);
      setIsSubmitting(false);
      onComplete(sanitizedGoals);
    }
  };
  
  const getCounterColor = () => {
    if (selectedGoals.length === 0) return "text-muted-foreground";
    if (selectedGoals.length < MAX_GOALS) return "text-primary";
    return "text-metabolic-600";
  };
  
  const canContinue = hasSelectedGoals || hasCustomGoal;
  const buttonText = hasCustomGoal 
    ? "Continue with Custom Goal" 
    : `Continue with ${selectedGoals.length} goal${selectedGoals.length !== 1 ? "s" : ""}`;
  
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="fixed inset-0 bg-background z-50 flex flex-col"
    >
      <div className="flex-1 px-6 py-8 overflow-y-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="mb-6"
        >
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            What are your health goals?
          </h1>
          <p className="text-muted-foreground">Select your top health priorities for this grocery run.</p>
        </motion.div>

        {/* Budget Counter */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }} 
          className="mb-4"
        >
        </motion.div>

        {/* Health Goal Cards Grid */}
        <div 
          className={`grid grid-cols-2 gap-3 transition-all duration-300 ${
            hasCustomGoal ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {goals.map((goal, index) => {
            const isSelected = selectedGoals.includes(goal.id);
            const isMuted = isAtLimit && !isSelected;
            const Icon = goal.icon;
            return (
              <motion.button 
                key={goal.id} 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: index * 0.05 }} 
                onClick={() => toggleGoal(goal.id)} 
                className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                  isSelected 
                    ? "border-primary bg-secondary" 
                    : isMuted 
                      ? "border-border bg-card opacity-50 cursor-not-allowed" 
                      : "border-border bg-card hover:border-muted-foreground/30"
                }`}
              >
                {isSelected && (
                  <motion.div 
                    initial={{ scale: 0 }} 
                    animate={{ scale: 1 }} 
                    className="absolute top-3 right-3 w-5 h-5 rounded-full gradient-primary flex items-center justify-center"
                  >
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </motion.div>
                )}
                <div className={`w-10 h-10 rounded-xl mb-3 flex items-center justify-center ${
                  isSelected ? "gradient-primary" : "bg-secondary"
                }`}>
                  <Icon className={`w-5 h-5 ${isSelected ? "text-primary-foreground" : "text-metabolic-500"}`} />
                </div>
                <h3 className="font-medium text-foreground text-sm mb-1">
                  {goal.label}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {goal.description}
                </p>
              </motion.button>
            );
          })}
        </div>

        {/* Divider */}
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 0.4 }}
          className="flex items-center gap-4 my-6"
        >
          <div className="flex-1 h-px bg-border" />
          <span className="text-sm text-muted-foreground font-medium">— OR —</span>
          <div className="flex-1 h-px bg-border" />
        </motion.div>

        {/* Custom Goal Textarea */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.45 }}
          className="space-y-2"
        >
          <label className="text-sm font-medium text-foreground">
            Custom Goal / Specific Request
          </label>
          <div className="relative">
            <Textarea
              value={customGoal}
              onChange={(e) => handleCustomGoalChange(e.target.value)}
              placeholder={`e.g., ${PLACEHOLDER_EXAMPLES[placeholderIndex]}...`}
              className={`min-h-[100px] resize-none bg-card border-2 transition-all pb-8 ${
                hasCustomGoal 
                  ? "border-primary focus:border-primary" 
                  : "border-border focus:border-muted-foreground/50"
              }`}
              maxLength={100}
            />
            <span className={`absolute bottom-2 right-3 text-xs ${
              customGoal.length > 90 ? "text-destructive" : "text-muted-foreground"
            }`}>
              {customGoal.length}/100
            </span>
          </div>
        </motion.div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.5 }} 
        className="p-6 pb-10 border-t border-border bg-background space-y-4"
      >
        <Button 
          onClick={handleContinue} 
          disabled={!canContinue || isSubmitting} 
          className="w-full h-14 text-base font-medium gradient-primary hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {isSubmitting ? "Processing..." : buttonText}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </motion.div>
    </motion.div>
  );
};
